import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

const PRIVANOT_PRESET = definePreset(Aura, {
    components: {
        button: {
            // Remove all button transitions to avoid unstyled flash on page load
            // TODO: this fix seems hacky. It could be a primeNG bug, mentioned here: https://github.com/primefaces/primeng/issues/19273
            css: ({ dt }) => `
                .p-button {
                    transition: none;
                }
                .p-button.pn-settled {
                    transition:
                        background ${dt('button.transition.duration')},
                        color ${dt('button.transition.duration')},
                        border-color ${dt('button.transition.duration')},
                        outline-color ${dt('button.transition.duration')},
                        box-shadow ${dt('button.transition.duration')};
                }
            `,
        },
        listbox: {
            css: ({ dt }) => `
                .p-listbox-option.p-listbox-option-selected {
                    border: 1px solid ${dt('primary.color')};
                }
            `,
        },
        tree: {
            node: { gap: '0.5rem' },
            css: () => `
                .p-tree > pn-icon-field {
                    display: block;
                    margin-block-end: 0.75rem;
                }
            `,
        },
    },
    semantic: {
        primary: {
            50: '#eafdff',
            100: '#ccfbff',
            200: '#99f8ff',
            300: '#5cf4ff',
            400: '#1aefff',
            500: '#00bac6',
            600: '#008f99',
            700: '#006970',
            800: '#004247',
            900: '#002629',
            950: '#001719',
        },
        secondary: {
            50: '#eaf0f4',
            100: '#d5dfe9',
            200: '#acbfd8',
            300: '#839fcc',
            400: '#5a80c0',
            500: '#3761a4',
            600: '#2d4f86',
            700: '#243d68',
            800: '#1a2b4a',
            900: '#111e35',
            950: '#0d1524',
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.500}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}',
                },
                secondary: {
                    color: '{secondary.500}',
                    contrastColor: '#ffffff',
                    hoverColor: '{secondary.600}',
                    activeColor: '{secondary.700}',
                },
                privanot: {
                    surfacePage: '#f5f3ff',
                    surfaceCard: '#ffffff',
                    surfaceGridLine: 'rgba(99, 102, 241, 0.06)',
                    borderSubtle: 'rgba(0, 0, 0, 0.18)',
                    borderStrong: 'rgba(0, 0, 0, 0.32)',
                    interactiveHoverBg: 'color-mix(in srgb, {primary.color} 10%, transparent)',
                    interactiveActiveBg: 'color-mix(in srgb, {primary.color} 15%, transparent)',
                    // Status pills — soft fill + dark same-hue text (light). Dark variants invert this (see dark.privanot).
                    pillSuccessText: '{emerald.800}',
                    pillSuccessBg: '{emerald.200}',
                    pillSuccessBorder: 'color-mix(in srgb, {emerald.800} 10%, transparent)',
                    pillWarningText: '{amber.500}',
                    pillWarningBg: '{amber.50}',
                    pillWarningBorder: 'color-mix(in srgb, {amber.400} 30%, transparent)',
                    pillErrorText: '{red.600}',
                    pillErrorBg: '{red.100}',
                    pillErrorBorder: 'color-mix(in srgb, {red.800} 25%, transparent)',
                    pillNeutralText: '{neutral.800}',
                    pillNeutralBg: '{neutral.200}',
                    pillNeutralBorder: 'color-mix(in srgb, {neutral.800} 10%, transparent)',
                    // Soft accent chips (stat-card icon badges) — soft fill + dark icon (matches the existing light look).
                    softPrimaryBg: '{primary.50}',
                    softPrimaryFg: '{primary.600}',
                    softWarningBg: '{amber.50}',
                    softWarningFg: '{amber.600}',
                    softSuccessBg: '{emerald.50}',
                    softSuccessFg: '{emerald.600}',
                    // Brand logo — recolorable two-tone mark + wordmark. Light: exact original brand colors (teal mark + slate text).
                    logoMark: '#00bac6',
                    logoText: '#2b4752',
                },
            },
            dark: {
                primary: {
                    color: '{primary.400}',
                    contrastColor: '{surface.900}',
                    hoverColor: '{primary.500}',
                    activeColor: '{primary.600}',
                },
                secondary: {
                    color: '{secondary.400}',
                    contrastColor: '{surface.900}',
                    hoverColor: '{secondary.500}',
                    activeColor: '{secondary.600}',
                },
                privanot: {
                    surfacePage: '#14161b',
                    surfaceCard: '#1c1f26',
                    surfaceGridLine: 'rgba(255, 255, 255, 0.04)',
                    borderSubtle: 'rgba(255, 255, 255, 0.18)',
                    borderStrong: 'rgba(255, 255, 255, 0.32)',
                    interactiveHoverBg: 'color-mix(in srgb, {primary.color} 14%, transparent)',
                    interactiveActiveBg: 'color-mix(in srgb, {primary.color} 20%, transparent)',
                    // Status pills — translucent same-hue fill + lighter same-hue text, so chips blend with the dark surface.
                    // Fill/border at "Medium" intensity so pills read clearly and differ from each other.
                    pillSuccessText: '{emerald.300}',
                    pillSuccessBg: 'color-mix(in srgb, {emerald.500} 24%, transparent)',
                    pillSuccessBorder: 'color-mix(in srgb, {emerald.400} 48%, transparent)',
                    pillWarningText: '{amber.300}',
                    pillWarningBg: 'color-mix(in srgb, {amber.500} 24%, transparent)',
                    pillWarningBorder: 'color-mix(in srgb, {amber.400} 48%, transparent)',
                    pillErrorText: '{red.300}',
                    pillErrorBg: 'color-mix(in srgb, {red.500} 24%, transparent)',
                    pillErrorBorder: 'color-mix(in srgb, {red.400} 48%, transparent)',
                    pillNeutralText: '{neutral.200}',
                    pillNeutralBg: 'color-mix(in srgb, {neutral.400} 20%, transparent)',
                    pillNeutralBorder: 'color-mix(in srgb, {neutral.400} 40%, transparent)',
                    // Soft accent chips (stat-card icon badges) — gentler than pills; translucent tint + bright icon.
                    softPrimaryBg: 'color-mix(in srgb, {primary.500} 18%, transparent)',
                    softPrimaryFg: '{primary.300}',
                    softWarningBg: 'color-mix(in srgb, {amber.500} 18%, transparent)',
                    softWarningFg: '{amber.300}',
                    softSuccessBg: 'color-mix(in srgb, {emerald.500} 18%, transparent)',
                    softSuccessFg: '{emerald.300}',
                    // Brand logo — recolorable two-tone mark + wordmark. Dark: brighter teal mark + near-white text.
                    logoMark: '#2ad4df',
                    logoText: '#f5f7fa',
                },
            },
        },
    },
});

export function providePrivanotTheme(overrides?: Parameters<typeof definePreset>[1]): EnvironmentProviders {
    const preset = overrides ? definePreset(PRIVANOT_PRESET, overrides) : PRIVANOT_PRESET;

    return makeEnvironmentProviders([providePrimeNG({ theme: { preset, options: { darkModeSelector: '.dark' } } })]);
}
